
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.tsx'
import './index.css'
import { supabase } from './integrations/supabase/client'
import { toast } from 'sonner'
import { isAuthCallback, handleAuthCallback, redirectAfterAuth } from './auth/utils/authCallback'
import { logWithTimestamp, AuthEvent, logAuthEvent } from './auth/utils/authUtils'

// Enhanced error handling for auth callbacks
if (isAuthCallback()) {
  logWithTimestamp('Auth callback detected in main.tsx');
  
  // Add a longer delay to ensure the browser has fully processed URL parameters
  setTimeout(() => {
    logWithTimestamp('Processing auth callback after delay');
    
    handleAuthCallback()
      .then((result) => {
        if (result.success) {
          toast.success("Successfully signed in!");
          // Redirect to /chat after successful setup
          redirectAfterAuth('/chat');
        } else {
          logWithTimestamp('Auth callback failed:', result.error);
          toast.error("Authentication failed. Please try again.");
          // Redirect to auth page on failure
          window.location.href = '/auth';
        }
      })
      .catch(error => {
        console.error("Fatal error during auth callback:", error);
        logWithTimestamp('Fatal error during auth callback:', error);
        toast.error("An unexpected error occurred during login");
        window.location.href = '/auth';
      });
  }, 500); // Increase delay to 500ms to ensure browser has fully processed URL
}

// Add enhanced debugging for auth state changes with metadata inspection
supabase.auth.onAuthStateChange((event, session) => {
  logWithTimestamp(`Auth Event in main.tsx: ${event}`);
  
  // Log the event with structured format
  logAuthEvent(
    event as AuthEvent, 
    session?.user?.id || null,
    session ? { email: session.user.email, provider: session.user.app_metadata.provider } : null
  );
  
  if (session) {
    logWithTimestamp("Session found in main.tsx, user:", session.user.email);
    logWithTimestamp("User metadata in main.tsx:", session.user.user_metadata);
    logWithTimestamp("App metadata in main.tsx:", session.user.app_metadata);
    logWithTimestamp(`Session expires at: ${new Date(session.expires_at! * 1000).toLocaleString()}`);
  } else {
    logWithTimestamp(`No session available in main.tsx after auth event: ${event}`);
  }
});

// Initial session check on app load
logWithTimestamp("Performing initial session check in main.tsx");
supabase.auth.getSession().then(({ data, error }) => {
  if (error) {
    console.error('Initial session check error in main.tsx:', error);
  } else {
    if (data.session) {
      logWithTimestamp('Initial session check in main.tsx: Session found');
      logWithTimestamp('Session user in main.tsx:', data.session.user.email);
      logWithTimestamp('Session user ID in main.tsx:', data.session.user.id);
    } else {
      logWithTimestamp('Initial session check in main.tsx: No session found');
    }
  }
});

createRoot(document.getElementById("root")!).render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
);
